import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import type { VideoLayout } from '../state.ts';

/** Reference-video compositing settings (mirrors the live overlay). */
export interface CompositeOptions {
  video: HTMLVideoElement;
  layout: VideoLayout;
  /** Video-layer opacity (overlay mode). */
  opacity: number;
  /** 3D-layer opacity (overlay mode). */
  sceneOpacity: number;
  /** Overlay mode: video above the 3D. */
  onTop: boolean;
  /** Video offset in TIMELINE frames. */
  frameDelta: number;
}

export interface VideoExportOptions {
  canvas: HTMLCanvasElement;
  /** Timeline fps — frame numbers below are in this rate. */
  fps: number;
  startFrame: number;
  endFrame: number;
  /** Output frame rate; defaults to `fps`. Lower = downsampled output. */
  exportFps?: number;
  /**
   * Output dimensions. Default: the canvas size. Must be given when compositing
   * split layouts, where the 3D canvas renders at HALF the output size.
   */
  outputWidth?: number;
  outputHeight?: number;
  /** Burn the reference video into the frames, matching the live overlay. */
  composite?: CompositeOptions;
  /** Step the animation+scene to an exact (possibly fractional) timeline frame. */
  stepToFrame: (frame: number) => void;
  onProgress?: (done: number, total: number) => void;
}

/** WebCodecs is required for offline encoding; gate the feature on it. */
export function isVideoExportSupported(): boolean {
  return typeof window !== 'undefined' && 'VideoEncoder' in window && window.isSecureContext;
}

/**
 * AVC levels by max luma sample count (coded area). The level is encoded as the
 * last byte of the codec string and caps the allowed resolution, so we must pick
 * one large enough for the canvas or configure() fails.
 */
const AVC_LEVELS: Array<{ hex: string; maxArea: number }> = [
  { hex: '28', maxArea: 2_097_152 }, // 4.0
  { hex: '32', maxArea: 5_652_480 }, // 5.0
  { hex: '33', maxArea: 9_437_184 }, // 5.1
  { hex: '34', maxArea: 9_437_184 }, // 5.2
  { hex: '3c', maxArea: 35_651_584 }, // 6.0
];

/** Pick the first AVC codec string the platform's VideoEncoder accepts. */
async function pickCodec(width: number, height: number, fps: number): Promise<string> {
  const area = width * height;
  const level = AVC_LEVELS.find((l) => area <= l.maxArea) ?? AVC_LEVELS[AVC_LEVELS.length - 1];
  // Try the smallest sufficient level first, then larger ones; high → main → baseline.
  const levelHexes = AVC_LEVELS.filter((l) => l.maxArea >= area).map((l) => l.hex);
  const profiles = ['6400', '4d00', '4200'];

  for (const hex of levelHexes) {
    for (const profile of profiles) {
      const codec = `avc1.${profile}${hex}`;
      try {
        const support = await VideoEncoder.isConfigSupported({
          codec,
          width,
          height,
          framerate: fps,
        });
        if (support.supported) return codec;
      } catch {
        /* try next */
      }
    }
  }
  return `avc1.6400${level.hex}`;
}

/**
 * Offline, frame-by-frame MP4 render of the marked segment (SPEC §3.5 Feature A).
 * Steps deterministically through [startFrame, endFrame], renders each frame, and
 * encodes it with WebCodecs + mp4-muxer so output is smooth regardless of system
 * lag and aligns frame-for-frame with the timeline marks.
 */
export async function exportVideo(opts: VideoExportOptions): Promise<void> {
  if (!isVideoExportSupported()) {
    throw new Error('Video export requires a Chromium browser with WebCodecs (secure context).');
  }

  const { canvas, fps, stepToFrame, onProgress, composite } = opts;
  const start = Math.min(opts.startFrame, opts.endFrame);
  const end = Math.max(opts.startFrame, opts.endFrame);
  if (end - start < 1) throw new Error('Marked range is empty — set Start and End frames first.');

  // Output frames sample the marked range's SECONDS at exportFps (≤ timeline fps).
  const outFps = Math.min(opts.exportFps ?? fps, fps);
  const rangeSec = (end - start) / fps;
  const frameCount = Math.max(1, Math.round(rangeSec * outFps));

  // H.264 4:2:0 needs even dimensions; copy each frame into an even-sized buffer.
  const width = (opts.outputWidth ?? canvas.width) - ((opts.outputWidth ?? canvas.width) % 2);
  const height = (opts.outputHeight ?? canvas.height) - ((opts.outputHeight ?? canvas.height) % 2);

  const scratch = document.createElement('canvas');
  scratch.width = width;
  scratch.height = height;
  const ctx = scratch.getContext('2d', { alpha: false })!;
  const compositor = composite ? createCompositor(composite, width, height, fps) : null;

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width, height, frameRate: outFps },
    fastStart: 'in-memory',
  });

  const codec = await pickCodec(width, height, outFps);

  // Capture async codec errors and surface them at the next checkpoint rather
  // than throwing inside the callback (which would just close the codec).
  let encoderError: Error | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => {
      encoderError = e as Error;
    },
  });

  const bitrate = Math.min(24_000_000, Math.round(width * height * outFps * 0.08));
  encoder.configure({ codec, width, height, bitrate, framerate: outFps });

  const frameDurationUs = 1_000_000 / outFps;
  const keyEvery = Math.max(1, Math.round(outFps * 2)); // keyframe every ~2s

  try {
    for (let i = 0; i < frameCount; i++) {
      if (encoderError) throw encoderError;
      const sec = start / fps + i / outFps;
      stepToFrame(sec * fps); // sets mixer time + renders synchronously
      if (compositor) {
        await compositor.draw(ctx, canvas, sec);
      } else {
        ctx.drawImage(canvas, 0, 0, width, height);
      }

      const videoFrame = new VideoFrame(scratch, {
        timestamp: Math.round(i * frameDurationUs),
        duration: Math.round(frameDurationUs),
      });
      encoder.encode(videoFrame, { keyFrame: i % keyEvery === 0 });
      videoFrame.close();

      onProgress?.(i + 1, frameCount);

      // Yield periodically so the encoder queue drains and the UI can paint.
      if (encoder.encodeQueueSize > 8) {
        await new Promise<void>((r) => setTimeout(r, 0));
      }
    }

    await encoder.flush();
    if (encoderError) throw encoderError;
    muxer.finalize();
  } finally {
    if (encoder.state !== 'closed') encoder.close();
  }

  const { buffer } = muxer.target as ArrayBufferTarget;
  const blob = new Blob([buffer], { type: 'video/mp4' });
  downloadBlob(blob, 'mocap_segment.mp4');
}

// --- Reference-video compositing --------------------------------------------------

/** Seek a (paused) video to `t` seconds and wait for the frame to decode. */
function seekVideo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 0.001) {
      resolve();
      return;
    }
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      video.removeEventListener('seeked', finish);
      resolve();
    };
    video.addEventListener('seeked', finish);
    // Long-GOP seeks can be slow but must never hang the export.
    setTimeout(finish, 3000);
    video.currentTime = t;
  });
}

/** Cover the target rect with a black backdrop + the video letterboxed inside. */
function drawVideoInRect(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  showVideo: boolean,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  if (!showVideo || !video.videoWidth || !video.videoHeight) return;
  // object-fit: contain
  const scale = Math.min(w / video.videoWidth, h / video.videoHeight);
  const vw = video.videoWidth * scale;
  const vh = video.videoHeight * scale;
  ctx.drawImage(video, x + (w - vw) / 2, y + (h - vh) / 2, vw, vh);
}

/**
 * Per-frame compositor matching the live overlay layouts:
 *  - pip:      3D full-frame, video in the bottom-right picture-in-picture box.
 *  - split-*:  3D in one half (the canvas renders at HALF the output size),
 *              video letterboxed in the other.
 *  - overlay:  both full-frame; layer order, per-layer opacity and the keyed
 *              (transparent) 3D background all honoured — the canvas keeps its
 *              alpha channel, so a keyed render composites exactly like the DOM.
 */
function createCompositor(c: CompositeOptions, width: number, height: number, fps: number) {
  // Overlay layers get their own canvas so per-layer opacity applies to the
  // (backdrop + video) unit, exactly like CSS opacity on the DOM layer.
  const layer = document.createElement('canvas');
  layer.width = width;
  layer.height = height;
  const layerCtx = layer.getContext('2d')!;

  async function draw(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    sec: number
  ): Promise<void> {
    const t = sec + c.frameDelta / fps;
    const inRange = t >= 0 && t <= (c.video.duration || 0);
    if (inRange) await seekVideo(c.video, t);

    switch (c.layout) {
      case 'pip': {
        ctx.drawImage(canvas, 0, 0, width, height);
        const margin = Math.round(width * 0.016);
        const w = Math.round(width * 0.32);
        const h = Math.round((w * 9) / 16);
        drawVideoInRect(ctx, c.video, inRange, width - w - margin, height - h - margin, w, h);
        break;
      }
      case 'split-lr': {
        const half = Math.floor(width / 2);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(canvas, 0, 0, half, height);
        drawVideoInRect(ctx, c.video, inRange, half, 0, width - half, height);
        break;
      }
      case 'split-tb': {
        const half = Math.floor(height / 2);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(canvas, 0, 0, width, half);
        drawVideoInRect(ctx, c.video, inRange, 0, half, width, height - half);
        break;
      }
      case 'overlay': {
        drawVideoInRect(layerCtx, c.video, inRange, 0, 0, width, height);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        if (c.onTop) {
          ctx.globalAlpha = c.sceneOpacity;
          ctx.drawImage(canvas, 0, 0, width, height);
          ctx.globalAlpha = c.opacity;
          ctx.drawImage(layer, 0, 0);
        } else {
          ctx.globalAlpha = c.opacity;
          ctx.drawImage(layer, 0, 0);
          // A keyed 3D render carries alpha, so this composites over the video.
          ctx.globalAlpha = c.sceneOpacity;
          ctx.drawImage(canvas, 0, 0, width, height);
        }
        ctx.globalAlpha = 1;
        break;
      }
    }
  }

  return { draw };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
